import React, { useRef, useEffect, useCallback, useState } from 'react';
import Chart from 'chart.js';
import { parse, stringify } from 'query-string';
import chartJsBarChartBackgroundPlugin from '../plugins/chartJsBarChartBackgroundPlugin';
import Icon from '../Icon/Icon';
import { stringifyFilter, stringToColor, format } from '../../utils';
import { useGlobalValue } from '../../state/GlobalProvider';
import styles from './EntitySummaryChart.module.sass';
const TICKS_FONT_SIZE = 12;

const transform = (data, colors = []) => {
  const labels = new Set();
  const legendColors = {};
  const legend = new Set();
  const chartData = [];
  const datasets = {}; // TODO: Fix mapping logic API -> Graph (Changed from Object of entries to array of objects) 

  data = data.map(innerData => (innerData.summary || []).reduce((sum, ele) => {
    sum[ele.key] = ele.value;
    return sum;
  }, {}));
  data.forEach((periodGroups, index) => {
    if (Object.keys(periodGroups).length) {
      chartData.push({
        label: index,
        groups: periodGroups
      });
    }
  });
  const numberOfColumns = chartData.length;
  let maxPerPeriod = new Array(numberOfColumns).fill(0);
  chartData.forEach(({
    label,
    groups
  }, index) => {
    labels.add(label);
    Object.entries(groups).sort(([a], [b]) => a.localeCompare(b)).forEach(([name, count]) => {
      const colorForName = stringToColor(name);
      legend.add(name);

      if (!datasets[name]) {
        datasets[name] = {
          data: new Array(numberOfColumns).fill(0),
          backgroundColor: [],
          label: name,
          borderWidth: {
            top: 1
          },
          borderColor: colors[0],
          xAxisID: 'bar-x-axis1',
          yAxisID: 'bar-y-axis1',
          total: 0
        };
      }

      legendColors[name] = colorForName;
      datasets[name].backgroundColor[index] = colorForName;
      datasets[name].data[index] = count;
      maxPerPeriod[index] += count;
    });
  });
  const datasetsValues = Object.values(datasets);
  const max = Math.max(...maxPerPeriod);
  const chartDatasets = numberOfColumns > 0 ? [...datasetsValues, {
    label: null,
    backgroundColor: colors[1],
    data: new Array(numberOfColumns).fill(max),
    xAxisID: 'bar-x-axis2',
    yAxisID: 'bar-y-axis2'
  }] : [];
  return {
    legend: [...legend].sort().map(label => ({
      label,
      color: legendColors[label]
    })),
    labels: [...labels].sort((a, b) => Number(a) - Number(b)),
    datasets: chartDatasets,
    max: maxPerPeriod
  };
};

const renderChart = (canvas, style, data, applyFilter, chartJsBarChartBackgroundPlugin) => {
  const ctx = canvas.getContext('2d');
  return new Chart(ctx, {
    type: 'bar',
    data: data,
    options: {
      onClick: function (e, t) {
        const activePoint = this.getElementAtEvent(e)[0];

        if (activePoint) {
          const data = activePoint._chart.data;
          const datasetIndex = activePoint._datasetIndex;
          const columnIndex = activePoint._index;
          const group = data.datasets[datasetIndex].label || undefined;
          const period = data.labels[columnIndex];
          applyFilter({
            target: {
              value: [period, group]
            }
          });
        }
      },
      layout: {
        padding: {
          top: 20
        }
      },
      hover: {
        // mode: null,
        animationDuration: 0,
        onHover: function (e) {
          const point = this.getElementAtEvent(e);
          e.target.style.cursor = point.length ? 'pointer' : 'default';
        }
      },
      tooltips: {
        enabled: true
      },
      animation: {
        duration: 0,
        onComplete: function () {
          const chartInstance = this.chart;
          const innerCtx = chartInstance.ctx;
          innerCtx.font = Chart.helpers.fontString(parseFloat(style.fontSize), style.fontWeight, style.fontFamily);
          innerCtx.textAlign = 'center';
          innerCtx.textBaseline = 'bottom';
          const bars = chartInstance.controller.getDatasetMeta(1).data;
          const columnWidth = canvas.offsetWidth / bars.length;
          const barWidth = columnWidth * 0.6;
          innerCtx.fillStyle = style.color;
          const rotation = columnWidth < 40 ? -30 * (Math.PI / 180) : 0;
          bars.forEach((bar, i) => {
            innerCtx.save();
            const x = bar._model.x;
            const value = data.max[i] || 0;
            innerCtx.translate(x, 26);
            innerCtx.rotate(rotation);
            value && innerCtx.fillText(format(value, 'short', null), 0, -5);
            innerCtx.restore();
          });
          innerCtx.fillStyle = 'white';
          this.data.datasets.forEach((dataset, i) => {
            const meta = chartInstance.controller.getDatasetMeta(i);
            meta.data.forEach(function (bar, index) {
              const value = dataset.label ? dataset.data[index] : null;
              const ratio = value ? value / data.max[index] : 0;
              const height = ratio * canvas.offsetHeight;

              if (value && height > 14 && barWidth > 24) {
                innerCtx.fillText(format(value, 'short', null), bar._model.x, bar._model.y + (bar._model.base - bar._model.y) / 2 + 5);
              }
            });
          });
        }
      },
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        display: false
      },
      scales: {
        xAxes: [{
          id: "bar-x-axis2",
          display: false,
          stacked: true,
          categoryPercentage: 1,
          barPercentage: 0.8
        }, {
          display: true,
          stacked: true,
          id: "bar-x-axis1",
          type: 'category',
          maxBarThickness: 50,
          categoryPercentage: 1.0,
          barPercentage: 0.6,
          gridLines: {
            color: style.borderColor,
            lineWidth: 1,
            offsetGridLines: true,
            zeroLineColor: 'transparent'
          },
          ticks: {
            fontColor: style.color
          },
          offset: true
        }],
        yAxes: [{
          stacked: false,
          display: false,
          id: "bar-y-axis2",
          ticks: {
            beginAtZero: true
          }
        }, {
          stacked: true,
          id: "bar-y-axis1",
          gridLines: {
            display: true,
            drawBorder: false,
            color: style.borderColor,
            zeroLineColor: style.borderColor
          },
          ticks: {
            padding: 0,
            fontStyle: 'normal',
            fontColor: style.fill,
            fontSize: TICKS_FONT_SIZE,
            beginAtZero: true,
            callback: function (label, index, labels) {
              if (index !== 0) {
                return '';
              }
            }
          }
        }]
      },
      plugins: {
        chartJsBarChartBackgroundPlugin: {
          color: '#edf1f4'
        }
      }
    }
  });
};

const EntitySummaryChart = props => {
  const {
    title,
    data,
    modifiers,
    anchor,
    availableFilters
  } = props;
  const {
    i18n,
    history
  } = useGlobalValue();
  const canvas = useRef(null);
  const [legends, setLegends] = useState([]);
  const modifiersClasses = (modifiers || []).map(m => styles[m]).filter(m => m).join(' ');
  const applyFilter = useCallback(e => {
    const [period, group] = e.target.value || [];
    const filter = []; // const availableGroups = (chartData.datasets[period] || {}).groups || []

    if (period !== undefined) {
      const periodFilter = (availableFilters || []).find(({
        name,
        prop
      }) => (name || prop || '').toLowerCase() === 'period');

      if (periodFilter) {
        filter.push({
          column: periodFilter.prop || 'period',
          filters: [{
            value: `${period}`
          }]
        });
      }
    }

    if (group !== undefined) {
      const typeFilter = (availableFilters || []).find(({
        name,
        prop
      }) => (name || prop || '').toLowerCase() === 'type');

      if (typeFilter) {
        filter.push({
          column: typeFilter.prop || 'type',
          filters: [{
            value: `${group}`
          }]
        });
      }
    }

    const filtersString = stringifyFilter(filter);
    const params = parse(history.location.search || '');
    const currentParams = { ...params,
      filter: filtersString,
      resetFilters: Date.now(),
      page: undefined
    };
    history.replace({
      pathname: history.location.pathname,
      search: stringify(currentParams),
      hash: anchor
    });
  }, [history, anchor, availableFilters]);
  const chartLegend = legends.map(({
    label,
    color
  }, index) => /*#__PURE__*/React.createElement("span", {
    key: index,
    className: styles['legend-item'],
    onClick: () => {
      applyFilter({
        target: {
          value: [undefined, label]
        }
      });
    }
  }, /*#__PURE__*/React.createElement("span", {
    className: styles['thumb'],
    style: {
      background: color
    }
  }), /*#__PURE__*/React.createElement("small", null, label)));
  useEffect(() => {
    let chart;

    if (canvas.current) {
      const style = canvas.current ? getComputedStyle(canvas.current) : {};
      const chartData = data && data.length && transform(data, [style.backgroundColor, style.fill]);
      setLegends((chartData || {}).legend || []);
      chart = canvas.current && chartData && renderChart(canvas.current, style, chartData, applyFilter, chartJsBarChartBackgroundPlugin);
    }

    return () => {
      chart && chart.destroy();
    };
  }, [canvas, data, applyFilter]);
  return data ? /*#__PURE__*/React.createElement("div", {
    className: `${styles['root']} ${modifiersClasses}`
  }, /*#__PURE__*/React.createElement("div", {
    className: styles['chart-container']
  }, /*#__PURE__*/React.createElement("h5", {
    className: `widget-title ${styles['title']}`
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Icon, {
    className: styles['icon'],
    name: "stack"
  }), /*#__PURE__*/React.createElement("div", null, title ? i18n`${title.join('')}` : ''))), /*#__PURE__*/React.createElement("div", {
    className: styles['chart-group']
  }, /*#__PURE__*/React.createElement("canvas", {
    ref: canvas
  })), /*#__PURE__*/React.createElement("div", {
    className: styles['chart-legend']
  }, chartLegend))) : null;
};

export default EntitySummaryChart;